import { Grid, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'
import { C1Button } from 'c1ui'
import { PluralFacetFilter } from 'components/core/search-filter/facet'
import { QueryFilter } from 'components/core/search-filter/query'
import { AccountTypeFilter } from 'components/core/search-filter/task/AccountTypeFilter'
import { TaskAgeFilter } from 'components/core/search-filter/task/age'
import { ApprovalTypeFilter } from 'components/core/search-filter/task/ApprovalTypeFilter'
import { DateRangeFilter } from 'components/core/search-filter/task/date-range'
import { EmploymentStatusFilter } from 'components/core/search-filter/task/EmploymentStatusFilter'
import { PendingActionFilter } from 'components/core/search-filter/task/PendingActionFilter'
import { TaskStateFilter } from 'components/core/search-filter/task/state'
import { TaskStateToggle } from 'components/core/search-filter/task/state-toggle'
import { TaskTypeFilter } from 'components/core/search-filter/task/type'
import { taskTypeFromString, taskTypeToString } from 'components/pages/task/type'
import { UserContext } from 'lib/context/app'
import { MetaFields, useSearch } from 'lib/search'
import { useRouter } from 'next/router'
import {
    PendingActionFilter as PendingActionFilterEnum,
    TaskSearchRequest,
    TaskSearchTermsRequest,
    TaskSearchTermsRequest_TermType as TermType,
} from 'pbts/c1/api/task/v1/search'
import { TaskType } from 'pbts/c1/api/task/v1/task'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import React, { useCallback, useContext, useMemo, useRef, useState } from 'react'
import { FormattedMessage, IntlShape, useIntl } from 'react-intl'
import { taskSearchAPI } from 'redux_toolkit/entitlements'
import { FilterPicker } from './FilterPicker'
import { defaultDisplayFilters, includeFilter, taskSearchFilters } from './TaskSearchFilter'

type FilterKeys = keyof Omit<TaskSearchRequest, MetaFields>
type FilterUpdate = Partial<Pick<TaskSearchRequest, FilterKeys>>
type FilterOnChange = {
    onChange: (updated: FilterUpdate, shouldPropage?: boolean) => void
}

type taskSearchFilterProps = {
    filter: TaskSearchRequest
    setFilter: (f: (filter: TaskSearchRequest) => TaskSearchRequest) => void
    displayFilters?: taskSearchFilters
    emptyFilter: Partial<TaskSearchRequest>
    setPage: (page: number) => void
    taskTypeOptions?: TaskType[]
    customizeFilters?: boolean
}

// maps request proto field name to filter id for filter picker URL params compatibility
const protoFieldToFilterId: Partial<Record<keyof TaskSearchRequest, string>> = {
    taskTypes: 'type',
    taskStates: 'status',
    appResourceTypeIds: 'resource-type',
    assigneesInIds: 'assignee',
    openerIds: 'opener',
    userEmploymentStatuses: 'user-employment-statuses',
    accountTypes: 'account-type',
    accountOwnerIds: 'subject',
    applicationIds: 'app',
    stepApprovalTypes: 'step-approval-types',
    olderThanDuration: 'older-than',
    appResourceIds: 'resource',
    actorId: 'show-only-completed-tasks',
    createdAfter: 'created-between',
    createdBefore: 'created-between',
    pendingActionFilter: 'pending-action',
}

export const getFilterKeyToLabelMap = (intl: IntlShape): Partial<Record<includeFilter, string>> => {
    // maps filter id to display label for the filter picker
    return {
        type: intl.formatMessage({ id: 'filter_label_task_type', defaultMessage: 'Task type' }),
        status: intl.formatMessage({ id: 'filter_label_status', defaultMessage: 'Status' }),
        subject: intl.formatMessage({ id: 'filter_label_account_owner', defaultMessage: 'Account owner' }),
        app: intl.formatMessage({ id: 'filter_label_app', defaultMessage: 'App' }),
        assignee: intl.formatMessage({ id: 'filter_label_assigned_to', defaultMessage: 'Assigned to' }),
        resource: intl.formatMessage({ id: 'filter_label_resource', defaultMessage: 'Resource' }),
        opener: intl.formatMessage({ id: 'filter_label_created_by', defaultMessage: 'Created by' }),
        'account-type': intl.formatMessage({ id: 'filter_label_account_type', defaultMessage: 'Account type' }),
        'created-between': intl.formatMessage({ id: 'filter_label_created_between', defaultMessage: 'Created between' }),
        'resource-type': intl.formatMessage({ id: 'filter_label_resource_types', defaultMessage: 'Resource types' }),
        'show-only-completed-tasks': intl.formatMessage({ id: 'filter_label_show_completed_tasks', defaultMessage: 'Show completed tasks' }),
        'older-than': intl.formatMessage({ id: 'filter_label_age', defaultMessage: 'Age' }),
        'step-approval-types': intl.formatMessage({ id: 'filter_label_approval_types', defaultMessage: 'Approval type' }),
        'user-employment-statuses': intl.formatMessage({ id: 'filter_label_user_hr_status', defaultMessage: 'User HR status' }),
        'pending-action': intl.formatMessage({ id: 'filter_label_pending_action', defaultMessage: 'Pending review' }),
    }
}

const CompletedToggle: React.FC<FilterOnChange & { filter: TaskSearchRequest }> = ({ filter, onChange }) => {
    const userContext = useContext(UserContext)
    const userId = userContext.user.id

    const viewMode: 'incomplete' | 'all' = filter.myWorkUserIds?.length > 0 ? 'all' : 'incomplete'

    return <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={(_, value): void => {
            if (value === 'incomplete') {
                onChange({
                    assigneesInIds: [userId],
                    includeActedAfter: Timestamp.fromDate(new Date()),
                    myWorkUserIds: undefined,
                })
            } else if (value === 'all') {
                onChange({
                    assigneesInIds: undefined,
                    includeActedAfter: undefined,
                    myWorkUserIds: [userId],
                })
            }
        }}
        size="small"
    >
        <ToggleButton value="incomplete">
            <FormattedMessage id="component_access_review_by_app_todo" defaultMessage="To do" />
        </ToggleButton>
        <ToggleButton value="all">
            <FormattedMessage id="component_access_review_by_app_all" defaultMessage="All" />
        </ToggleButton>
    </ToggleButtonGroup>
}

const urlEncode = (k, v): Array<string> | string => {
    return k === 'taskTypes' ? v.map(taskTypeToString) : v
}

const unassignedOption = { value: 'none', displayName: 'Unassigned', iconUrl: 'icon:user:Unassigned', count: 1 }

export const ContextualTaskSearchFilter: React.FC<taskSearchFilterProps> = ({
    filter,
    setFilter,
    displayFilters = defaultDisplayFilters,
    emptyFilter,
    setPage,
    taskTypeOptions,
    customizeFilters = false,
}) => {
    const [visibleFilters, setVisibleFilters] = useState<string[]>(customizeFilters ? [] : displayFilters)
    const stackRef = useRef<HTMLDivElement>(null)
    const intl = useIntl()
    const router = useRouter()
    const filterKeyToLabel = getFilterKeyToLabelMap(intl)

    const { canClear, clear, onChange: _onChange, onQueryChange } = useSearch(
        TaskSearchRequest,
        filter,
        setFilter,
        emptyFilter,
        urlEncode,
        displayFilters.includes('status-toggle') ? ['taskStates' as const] : [],
    )

    const onChange: FilterOnChange['onChange'] = useCallback(
        (updated, ignoreMe) => {
            setPage(0)
            _onChange(updated, ignoreMe)
        },
        [_onChange, setPage],
    )

    const filters: React.ReactElement[] = useMemo(() => {
        const f: React.ReactElement[] = []

        if (displayFilters.includes('status-toggle')) {
            f.push(
                <Grid key="status-toggle" item>
                    <TaskStateToggle
                        selected={filter?.taskStates}
                        onChange={(taskStates, loading): void => onChange({ taskStates }, loading)}
                    />
                </Grid>,
            )
        }

        if (displayFilters.includes('query')) {
            f.push(
                <Grid key="query" item>
                    <QueryFilter
                        autoFocus
                        query={filter?.query || ''}
                        setQuery={(q): void => onQueryChange(q as unknown as string, false)}
                        onChange={onQueryChange}
                    />
                </Grid>,
            )
        }

        if (displayFilters.includes('show-only-completed-tasks')) {
            f.push(
                <Grid key="completedToggle" item>
                    <CompletedToggle onChange={onChange} filter={filter} />
                </Grid>,
            )
        }

        if (customizeFilters) {
            f.push(<Grid key="filter-picker" item>
                <FilterPicker
                    filterOptions={displayFilters.filter((fName: string) => fName !== 'query' && fName !== 'status-toggle')}
                    filterKeyToLabel={getFilterKeyToLabelMap(intl)}
                    onSelectedFiltersChange={setVisibleFilters}
                    onFilterChange={(fieldsToClear): void => onChange(fieldsToClear as FilterUpdate)}
                    request={TaskSearchRequest}
                    fieldToFilterMap={protoFieldToFilterId}
                    urlDecode={(k, v): unknown => (
                        k === 'taskTypes' && Array.isArray(v)
                            ? v.map(taskTypeFromString)
                            : v
                    )}
                    id={`${router.pathname}-task-search-filter`}
                />
            </Grid>)
        }

        if (visibleFilters.includes('type')) {
            f.push(
                <Grid key="type" item>
                    <TaskTypeFilter
                        selected={filter?.taskTypes}
                        onChange={(taskTypes, loading): void => onChange({ taskTypes }, loading)}
                        options={taskTypeOptions}
                    />
                </Grid>,
            )
        }
        if (visibleFilters.includes('subject')) {
            f.push(
                <Grid key="requestedFor" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'accountOwnerIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.ACCOUNT_OWNERS_IDS}
                        filterKey="accountOwnerIds"
                        label={filterKeyToLabel.subject || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                        widePopper
                    />
                </Grid>,
            )
        }

        // Doesn't currently work
        // if (displayFilters.includes('app-user-with-context')) {
        //     f.push(
        //         <Grid key="appUser" item>
        //             <PluralFacetFilter<TaskSearchTermsRequest, 'appUserSubjectIds'>
        //                 onChange={onChange}
        //                 filter={filter}
        //                 term={TermType.APP_USER_SUBJECT_IDS}
        //                 filterKey="appUserSubjectIds"
        //                 label={intl.formatMessage({
        //                     id: 'task_user_filter_app_user',
        //                     defaultMessage: 'Account',
        //                 })}
        //                 hook={taskSearchAPI.useLazyGetSearchTerms}
        //                 widePopper
        //             />
        //         </Grid>,
        //     )
        // }

        if (visibleFilters.includes('app')) {
            f.push(
                <Grid key="app" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'applicationIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.APPLICATION_IDS}
                        filterKey="applicationIds"
                        label={filterKeyToLabel.app || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                        widePopper
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('status')) {
            f.push(
                <Grid key="status" item>
                    <TaskStateFilter
                        selected={filter?.taskStates}
                        onChange={(taskStates, loading): void => onChange({ taskStates }, loading)}
                    />
                    {/*
                        // `Status` needs to be mapped to the enum value by SQL to make this go
                        <PluralFacetFilter<TaskSearchTermsRequest>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.STATUS}
                        filterKey="taskStates"
                        label={intl.formatMessage({
                            id: 'task_user_filter_status',
                            defaultMessage: 'Status',
                        })}
                        hook={useLazyTaskSearchTerms}
                    /> */}
                </Grid>,
            )
        }

        if (visibleFilters.includes('older-than')) {
            f.push(
                <Grid key="older-than" item>
                    <TaskAgeFilter
                        selected={filter?.olderThanDuration}
                        onChange={(olderThanDuration, loading): void => onChange({ olderThanDuration }, loading)}
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('assignee')) {
            f.push(
                <Grid key="assignedTo" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'assigneesInIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.ASSIGNEE_IN_IDS}
                        filterKey="assigneesInIds"
                        label={filterKeyToLabel.assignee || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                        widePopper
                        showCount={false}
                        injectOption={unassignedOption}
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('resource')) {
            f.push(
                <Grid key="appResourceIds" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'appResourceIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.APP_RESOURCE_IDS}
                        filterKey="appResourceIds"
                        label={filterKeyToLabel.resource || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                        widePopper
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('resource-type')) {
            f.push(
                <Grid key="appResourceTypeIds" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'appResourceTypeIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.APP_RESOURCE_TYPE_IDS}
                        filterKey="appResourceTypeIds"
                        label={filterKeyToLabel['resource-type'] || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('step-approval-types')) {
            f.push(
                <Grid key="stepApprovalTypes" item>
                    <ApprovalTypeFilter
                        selected={filter?.stepApprovalTypes}
                        onChange={(types, loading): void => onChange({ stepApprovalTypes: types }, loading)}
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('opener')) {
            f.push(
                <Grid key="openers" item>
                    <PluralFacetFilter<TaskSearchTermsRequest, 'openerIds'>
                        onChange={onChange}
                        filter={filter}
                        term={TermType.OPENER_IDS}
                        filterKey="openerIds"
                        label={filterKeyToLabel.opener || ''}
                        hook={taskSearchAPI.useLazyGetSearchTerms}
                        widePopper
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('user-employment-statuses')) {
            f.push(
                <Grid key="userEmploymentStatuses" item>
                    <EmploymentStatusFilter
                        selected={filter?.userEmploymentStatuses}
                        onChange={(types, loading): void => onChange({ userEmploymentStatuses: types }, loading)}
                    />
                </Grid>,
            )
        }

        if (visibleFilters.includes('account-type')) {
            f.push(<Grid key="account-type" item>
                <AccountTypeFilter
                    selected={filter?.accountTypes}
                    onChange={(types, loading): void => onChange({ accountTypes: types }, loading)}
                />
            </Grid>)
        }

        if (visibleFilters.includes('pending-action')) {
            f.push(<Grid key="pending-action" item>
                <PendingActionFilter
                    selected={filter?.pendingActionFilter}
                    onChange={(values, loading): void => {
                        const pendingActionFilter = values && values.length > 0
                            ? values[0]
                            : PendingActionFilterEnum.UNSPECIFIED
                        onChange({ pendingActionFilter }, loading)
                    }}
                />
            </Grid>)
        }

        if (visibleFilters.includes('created-between')) {
            f.push(
                <Grid key="created-between" item>
                    <DateRangeFilter
                        startDate={filter?.createdAfter}
                        endDate={filter?.createdBefore}
                        onChange={(range, loading): void => {
                            onChange({
                                createdAfter: range.startDate,
                                createdBefore: range.endDate,
                            }, loading)
                        }}
                    />
                </Grid>,
            )
        }

        return f
    }, [JSON.stringify(displayFilters), filter, visibleFilters]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Stack
            ref={stackRef}
            direction="row"
            spacing={1}
            sx={{
                display: 'flex',
                flex: 1,
            }}
        >
            <Grid container item spacing={1} direction="row" alignItems="center">
                {filters}
                {canClear && displayFilters.length > 1 && (
                    <Grid item>
                        <C1Button onClick={clear} variant="text" color="primary">
                            <FormattedMessage id="components_task_search_index_clear" defaultMessage="Clear" />
                        </C1Button>
                    </Grid>
                )}
            </Grid>
        </Stack>
    )
}
